import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * CollapsibleTable - En tabell med kollapsbare kolonner
 * 
 * Props:
 * - columns: Array av kolonner [{key, label, minWidth, render}]
 *   Alle kolonner er automatisk kollapsbare
 * - data: Array av data-objekter
 * - actions: Funksjoner for handlinger (render-funksjon som mottar row)
 * - onRowDoubleClick: Callback når rad dobbeltklikkes (valgfri)
 */
const CollapsibleTable = ({ columns, data, actions, onRowDoubleClick }) => {
  const { t } = useTranslation();
  const [collapsedColumns, setCollapsedColumns] = useState({});

  const toggleColumn = (key) => {
    setCollapsedColumns(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="responsive-table-wrapper" style={{ overflowX: 'auto' }}>
      <table className="responsive-table" style={{ minWidth: '100%' }}>
        <thead>
          <tr>
            {columns.map((col, idx) => (
              <th 
                key={col.key}
                style={{
                  position: 'relative',
                  padding: '0.5rem 0.25rem',
                  paddingLeft: idx === 0 ? '0.75rem' : '0.25rem',
                  paddingRight: idx === columns.length - 1 ? '0.75rem' : '0.25rem'
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  gap: '0.15rem'
                }}>
                  <span style={{
                    display: 'block',
                    lineHeight: '1.2',
                    fontSize: '0.875rem',
                    maxWidth: '100px'
                  }}>
                    {col.label}
                  </span>
                  <button
                    onClick={() => toggleColumn(col.key)}
                    style={{
                      background: 'rgba(80, 227, 194, 0.1)',
                      border: '1px solid rgba(80, 227, 194, 0.3)',
                      borderRadius: '2px',
                      padding: '0.1rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#50E3C2',
                      transition: 'all 0.2s',
                      flexShrink: 0,
                      lineHeight: 0
                    }}
                    title={collapsedColumns[col.key] ? 'Utvid kolonne' : 'Minimer kolonne'}
                  >
                    {collapsedColumns[col.key] ? (
                      <ChevronRight size={8} />
                    ) : (
                      <ChevronLeft size={8} />
                    )}
                  </button>
                </div>
              </th>
            ))}
            {actions && <th style={{ minWidth: '120px', padding: '0.5rem 0.75rem 0.5rem 0.25rem' }}>{t('common.actions')}</th>}
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => (
            <tr 
              key={row.id || idx}
              onDoubleClick={() => onRowDoubleClick && onRowDoubleClick(row)}
              style={{
                cursor: onRowDoubleClick ? 'pointer' : 'default'
              }}
            >
              {columns.map((col, colIdx) => (
                <td 
                  key={col.key}
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: collapsedColumns[col.key] ? '40px' : (col.minWidth || 'auto'),
                    padding: '0.5rem 0.25rem',
                    paddingLeft: colIdx === 0 ? '0.75rem' : '0.25rem',
                    paddingRight: colIdx === columns.length - 1 ? '0.75rem' : '0.25rem'
                  }}
                >
                  {col.render ? col.render(row) : row[col.key]}
                </td>
              ))}
              {actions && (
                <td style={{ padding: '0.5rem 0.75rem 0.5rem 0.25rem' }}>
                  <div style={{ 
                    display: 'flex', 
                    gap: '0.25rem',
                    flexWrap: 'wrap'
                  }}>
                    {actions(row)}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CollapsibleTable;
